//import { Icon } from 'foxd';
import { Button } from '@fox/atoms/src/button/index';
import { Icon, IconName } from '@fox/atoms/src/icon/index';
import { LabeledCheckbox } from '@fox/atoms/src/labeled-checkbox/index';
import { PopoverScrollable } from '@fox/atoms/src/popover-scrollable/index';
import { TextField } from '@fox/atoms/src/text-field/index';
import { Typography } from '@fox/atoms/src/typography/index';
import { SpaceBetweenRow } from '@fox/layout/src/flex';
import { ReactElement, ReactNode, useState } from 'react';

type ChecklistItem = {
	id: string;
	title: ReactNode;
	icon?: IconName;
};

export interface ChecklistProps {
	/** list of columns */
	options: Array<ChecklistItem>;

	/** The list of IDs of the checklist items that have been checked
	 */
	selected: Array<string>;

	/** Event handler for updating the checked lists. */
	onSelect: (savedState: Array<string>) => void;

	/** side effects when reset button is clicked */
	onReset?: () => void;

	defaultIcon?: IconName;

	/** Allowing to disable last checkbos in case if rest are already selected, ex - hide columns */
	disableLast?: boolean;
}

type ChecklistPopoverOverlayProps = ChecklistProps & {
	/** the title of the checklist */
	title: string;
};

type ChecklistPopoverProps = ChecklistPopoverOverlayProps & {
	/** side effects when the popover visibility changes */
	onVisibleChange?: (visible: boolean) => void;

	children: ReactElement;
};

export function Checklist({ options, selected, onSelect, defaultIcon, disableLast }: ChecklistProps) {
	const optionsLength = options?.length - selected?.length === 1 || options.length === 1;
	const filterLastOption = options.filter(o => !selected.includes(o.id));

	return (
		<>
			{options.map(o => (
				<div key={`checklist-item-${o.id}`} style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
					<div style={{ display: 'flex', alignItems: 'center', marginRight: '0.2rem' }}>
						<Icon iconName={o.icon ?? defaultIcon ?? 'ShortText'} sx={{ marginRight: '5px' }} />
					</div>
					<LabeledCheckbox
						label={o.title}
						title={typeof o.title === 'string' ? o.title : undefined}
						checkboxProps={{ style: { marginLeft: 'auto' }, disableRipple: true, color: 'info' }}
						labelPlacement="start"
						style={{
							width: '100%',
							marginLeft: '0',
							cursor: disableLast
								? optionsLength
									? o.id === filterLastOption[0]?.id
										? 'not-allowed'
										: 'pointer'
									: 'pointer'
								: 'pointer'
						}}
						sx={{
							'& .MuiTypography-root': {
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
								overflow: 'hidden',
								maxWidth: '20rem'
							}
						}}
						checked={selected.includes(o.id)}
						disabled={disableLast ? (optionsLength ? (o.id === filterLastOption[0]?.id ? true : false) : false) : false}
						onChange={(_, checked) => {
							// we want to be 100% sure the existing list doesn't already have our item, so we can pre-filter and then add it back in
							const without = selected.filter(a => a != o.id);
							onSelect(checked ? [...without, o.id] : without);
						}}
					/>
				</div>
			))}
		</>
	);
}

/** A component that renders a checklist popover, which can be used as the overlay in a popover
 * <example>
 * @example
 * <PopoverScrollable overlay={<ChecklistPopoverOverlay />} onVisibleChange={onVisibleChange} placement="bottom-start">
 *   {button}
 * </PopoverScrollable>
 *
 * @see PinPopover, HidePopover for usage examples
 */
export function ChecklistPopoverOverlay({ title, options, ...rest }: ChecklistPopoverOverlayProps) {
	const [search, setSearch] = useState<string>('');
	const filteredOptions = options?.filter(option => option?.title?.toString()?.toLowerCase()?.includes(search?.toLowerCase()));
	const allSelected = options?.every(o => rest.selected?.includes(o.id));
	return (
		<div style={{ display: 'flex', flexDirection: 'column' }}>
			{options?.length > 10 ? (
				<div style={{ paddingLeft: '1.5rem', paddingTop: '1rem', paddingRight: '1.5rem' }}>
					<TextField
						size="small"
						fullWidth
						value={search}
						onChange={v => setSearch(v.target.value)}
						placeholder={'Search Columns'}
						//variant="standard"
						type={'search'}
					/>
				</div>
			) : (
				<></>
			)}
			<div style={{ minWidth: '17rem', padding: '1rem 1.5rem ' }}>
				<SpaceBetweenRow style={{ marginBottom: '0.5rem' }}>
					<Typography variant={'h6'}>{title}</Typography>
					<Button
						size="small"
						variant="tertiary"
						onClick={() => {
							if (allSelected) rest.onSelect([]);
							else rest.onSelect(options.map(o => o.id));
						}}
					>
						{allSelected ? 'Unselect' : 'Select'} All
					</Button>
				</SpaceBetweenRow>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<Checklist {...rest} options={filteredOptions} />
				</div>
			</div>
			<hr style={{ width: '100%', border: '1px solid rgba(0, 0, 0, 0.12)' }} />
			<div style={{ minWidth: '17rem', padding: '0 1.5rem 1rem', display: 'flex' }}>
				<Button
					color="error"
					style={{ width: '100%', height: 42, margin: '0 auto' }}
					onClick={() => {
						setSearch('');
						rest.onSelect([]);
					}}
				>
					Reset
				</Button>
			</div>
		</div>
	);
}

export const ChecklistPopover = ({ onVisibleChange, children, ...rest }: ChecklistPopoverProps) => {
	return (
		<PopoverScrollable overlay={<ChecklistPopoverOverlay {...rest} />} onVisibleChange={onVisibleChange} placement="bottom-start">
			{children}
		</PopoverScrollable>
	);
};
